/**
 * SponsorsPage.jsx — Grid of sponsor cards with logos
 */
import { useEffect, useState, useRef } from 'react';
import { Award, Plus, Search, Trash2, ExternalLink, Edit2, Upload, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const TIER_COLORS = {
  title:  '#f5c518',
  gold:   '#f97316',
  silver: '#8a93a3',
  team:   '#3b82f6',
};
const getTierColor = (typeId) => TIER_COLORS[typeId] || '#8b5cf6';

export default function SponsorsPage() {
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin = isAdmin();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token = () => localStorage.getItem('btccp_access_token');

  const [sponsors, setSponsors]         = useState([]);
  const [sponsorTypes, setSponsorTypes] = useState([]);
  const [tournaments, setTournaments]   = useState([]);
  const [teams, setTeams]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [viewTid, setViewTid]           = useState('init'); // 'init' → auto-picks current tournament

  // Create dialog
  const [createOpen, setCreateOpen]   = useState(false);
  const [creating, setCreating]       = useState(false);
  const [form, setForm]               = useState({ tournament_id:'', team_id:'', type_id:'', tagline:'', website_url:'' });

  // Edit sponsor state
  const [editTarget, setEditTarget]   = useState(null);
  const [editForm, setEditForm]       = useState({ name:'', tagline:'', website_url:'', type_id:'', logo_url:'', team_name:'' });
  // Member autocomplete for name field
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showNameSug, setShowNameSug]         = useState(false);
  const nameSugRef = useRef(null);
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  const [editLogoB64, setEditLogoB64]         = useState(null);
  const editLogoRef = useRef(null);
  const [editSaving, setEditSaving]   = useState(false);

  // Fetch member suggestions when typing in the Name field
  const fetchNameSuggestions = async (q) => {
    if (q.length < 2) { setNameSuggestions([]); setShowNameSug(false); return; }
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/players?q=${encodeURIComponent(q)}&limit=8`,
        { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) {
        const players = await res.json();
        setNameSuggestions(players);
        setShowNameSug(players.length > 0);
      }
    } catch (_) {}
  };
  const [memberSearch, setMemberSearch]     = useState('');
  const [memberResults, setMemberResults]   = useState([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const memberDebounce = useRef(null);

  useEffect(() => {
    Promise.allSettled([fetchTournaments(), fetchSponsorTypes(), fetchTeams()]);
  }, [orgSlug]);

  // Auto-select the most recent non-test, non-completed tournament once tournaments load
  useEffect(() => {
    if (!tournaments.length) return;
    if (viewTid !== 'init') return; // already set by user interaction
    const active = tournaments
      .filter(t => !t.is_test && t.status !== 'completed')
      .sort((a, b) => (b.season || '').localeCompare(a.season || ''));
    const best = active[0] || tournaments.filter(t => !t.is_test)[0] || tournaments[0];
    if (best) setViewTid(best.id);
  }, [tournaments]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewTid && viewTid !== 'init') fetchSponsors();
  }, [viewTid]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTournaments = async () => {
    try { const r = await api.get(`/orgs/${orgSlug}/tournaments`); setTournaments(Array.isArray(r.data)?r.data:[]); } catch(_) {}
  };
  const fetchSponsorTypes = async () => {
    try { const r = await api.get(`/orgs/${orgSlug}/sponsor-types`); setSponsorTypes(Array.isArray(r.data)?r.data:[]); } catch(_) {}
  };
  const fetchTeams = async () => {
    try { const r = await api.get(`/orgs/${orgSlug}/teams`); setTeams(Array.isArray(r.data)?r.data:[]); } catch(_) {}
  };
  const fetchSponsors = async () => {
    try {
      setLoading(true);
      const params = (viewTid && viewTid !== 'all' && viewTid !== 'init') ? { tournament_id: viewTid } : {};
      const r = await api.get(`/orgs/${orgSlug}/sponsors`, { params });
      setSponsors(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error('Failed to load sponsors'); }
    finally { setLoading(false); }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2*1024*1024) { toast.error('Logo must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = ev => { setLogoB64(ev.target.result); setLogoPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleMemberSearch = (q) => {
    setMemberSearch(q);
    setSelectedMember(null);
    clearTimeout(memberDebounce.current);
    if (!q.trim()) { setMemberResults([]); return; }
    memberDebounce.current = setTimeout(async () => {
      setMemberSearching(true);
      try {
        const r = await api.get(`/orgs/${orgSlug}/players`, { params: { q: q.trim(), limit: 20 } });
        setMemberResults(Array.isArray(r.data) ? r.data : []);
      } catch(_) {}
      finally { setMemberSearching(false); }
    }, 300);
  };

  const handleCreate = async () => {
    if (!form.tournament_id) { toast.error('Select a tournament'); return; }
    if (!selectedMember)     { toast.error('Select a sponsor member'); return; }
    if (!form.team_id)       { toast.error('Select a team to link'); return; }
    setCreating(true);
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/sponsors`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournament_id: form.tournament_id,
          member_id:     selectedMember.id,
          team_id:       form.team_id,
          type_id:       form.type_id || undefined,
          tagline:       form.tagline || undefined,
          website_url:   form.website_url || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`${data.name} added as sponsor`);
      resetCreate(); setCreateOpen(false); fetchSponsors();
    } catch { toast.error('Failed'); }
    finally { setCreating(false); }
  };

  const openEdit = (sponsor) => {
    setEditTarget(sponsor);
    setEditForm({ name: sponsor.name || '', tagline: sponsor.tagline || '', website_url: sponsor.website_url || '', type_id: sponsor.type_id || '', logo_url: sponsor.logo_url || '', team_name: sponsor.team_name || '' });
    setEditLogoPreview(sponsor.logo_url || sponsor.team_logo_url || null);
    setEditLogoB64(null);
  };

  const handleEditLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2*1024*1024) { toast.error('Logo must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = ev => { setEditLogoB64(ev.target.result); setEditLogoPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/sponsors/${editTarget.id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editForm, logo_url: editLogoB64 || editForm.logo_url || undefined }),
      });
      const data = await res.json();
      if (res.ok) { toast.success('Sponsor updated'); setEditTarget(null); fetchSponsors(); }
      else toast.error(data.detail || 'Failed to update');
    } catch (_) { toast.error('Failed to update'); }
    finally { setEditSaving(false); }
  };

  const handleDelete = async (id) => {    if (!confirm('Delete this sponsor?')) return;
    try { await api.delete(`/orgs/${orgSlug}/sponsors/${id}`); toast.success('Removed'); fetchSponsors(); }
    catch { toast.error('Failed'); }
  };

  const resetCreate = () => {
    setForm({ tournament_id:'', team_id:'', type_id:'', tagline:'', website_url:'' });
    setMemberSearch(''); setSelectedMember(null); setMemberResults([]);
  };

  const getTournamentName = (tid) => tournaments.find(t => t.id === tid)?.name || '—';

  const filtered = sponsors.filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase()) || s.team_name?.toLowerCase().includes(search.toLowerCase())
  );

  const TIER_OPTIONS = [
    { id:'title',  name:'Title Sponsor' },
    { id:'gold',   name:'Gold Sponsor'  },
    { id:'silver', name:'Silver Sponsor'},
    { id:'team',   name:'Team Sponsor'  },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-sponsors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Sponsors</h1>
          <p className="text-[#8a93a3]">Tournament sponsors linked to teams</p>
        </div>
        {admin && (
          <Button onClick={() => { resetCreate(); setCreateOpen(true); }}
            className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] w-fit"
            data-testid="add-sponsor-button">
            <Plus className="h-4 w-4 mr-2"/>Add Sponsor
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Tournament selector */}
        {tournaments.length > 0 && (
          <select
            value={viewTid === 'init' ? '' : viewTid}
            onChange={e => setViewTid(e.target.value || 'all')}
            className="bg-[#161a26] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-2 text-sm focus:border-[#f5c518] focus:outline-none"
          >
            <option value="">All Tournaments</option>
            {tournaments
              .filter(t => !t.is_test)
              .map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            {tournaments.some(t => t.is_test) && (
              <optgroup label="── Test ──">
                {tournaments.filter(t => t.is_test).map(t => (
                  <option key={t.id} value={t.id}>🧪 {t.name}</option>
                ))}
              </optgroup>
            )}
          </select>
        )}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]"/>
          <Input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search sponsors or teams..."
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7]"/>
        </div>
      </div>

      {/* Sponsor grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i=><Skeleton key={i} className="h-48 bg-[#1d2230] rounded-2xl"/>)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-12 text-center">
            <Award className="h-12 w-12 text-[#8a93a3] mx-auto mb-4"/>
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">No sponsors yet</h3>
            <p className="text-[#8a93a3] mb-4">Add a sponsor by selecting a member and linking them to a team</p>
            {admin && (
              <Button onClick={()=>{resetCreate();setCreateOpen(true);}} className="bg-[#f5c518] text-[#0b0f18]">
                <Plus className="h-4 w-4 mr-2"/>Add Sponsor
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map(sponsor => {
            const tierColor = getTierColor(sponsor.type_id);
            const tierName = [...TIER_OPTIONS, ...(sponsorTypes||[])].find(t => t.id === sponsor.type_id)?.name;
            return (
              <Card key={sponsor.id}
                className="bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.3)] transition-all hover:-translate-y-1 overflow-hidden group">
                {/* Logo area — uses the sponsor's own logo if uploaded, otherwise team logo */}
                <div className="relative h-36 bg-[#10131c] flex items-center justify-center overflow-hidden"
                  style={{ borderBottom: `2px solid ${sponsor.team_color || tierColor}30` }}>
                  {(sponsor.logo_url || sponsor.team_logo_url)?.startsWith('data:') ? (
                    <img src={sponsor.logo_url || sponsor.team_logo_url} alt={sponsor.name}
                      className="w-full h-full object-contain p-4"/>
                  ) : (sponsor.logo_url || sponsor.team_logo_url) ? (
                    <img src={sponsor.logo_url || sponsor.team_logo_url} alt={sponsor.name}
                      className="w-full h-full object-contain p-4"/>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-16 w-16 rounded-2xl flex items-center justify-center font-black text-3xl text-white"
                        style={{ backgroundColor: sponsor.team_color || tierColor }}>
                        {sponsor.team_name?.[0]?.toUpperCase() || sponsor.name?.[0]?.toUpperCase() || 'S'}
                      </div>
                    </div>
                  )}
                  {/* Tier badge top-right */}
                  {tierName && (
                    <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full font-semibold"
                      style={{ backgroundColor: `${tierColor}20`, color: tierColor, border: `1px solid ${tierColor}40` }}>
                      {tierName}
                    </span>
                  )}
                  {/* Action buttons on hover — admins only */}
                  {admin && (
                  <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(sponsor)}
                      className="h-6 w-6 rounded-full bg-[#1d2230]/80 flex items-center justify-center text-[#8a93a3] hover:text-[#f5c518]">
                      <Edit2 className="h-3 w-3"/>
                    </button>
                    {sponsor.website_url && (
                      <button onClick={() => window.open(sponsor.website_url,'_blank')}
                        className="h-6 w-6 rounded-full bg-[#1d2230]/80 flex items-center justify-center text-[#8a93a3] hover:text-[#f0f2f7]">
                        <ExternalLink className="h-3 w-3"/>
                      </button>
                    )}
                    <button onClick={() => handleDelete(sponsor.id)}
                      className="h-6 w-6 rounded-full bg-[#1d2230]/80 flex items-center justify-center text-[#8a93a3] hover:text-[#ef4444]">
                      <Trash2 className="h-3 w-3"/>
                    </button>
                  </div>
                  )}
                </div>

                {/* Info below logo */}
                <CardContent className="p-3">
                  <p className="font-bold text-[#f0f2f7] text-sm truncate">{sponsor.name}</p>
                  {sponsor.team_name && (
                    <p className="text-xs mt-0.5 flex items-center gap-1">
                      <span className="text-[#f5c518] font-medium">Team:</span>
                      <span className="text-[#8a93a3] truncate">{sponsor.team_name}</span>
                    </p>
                  )}
                  {sponsor.tagline && (
                    <p className="text-[10px] text-[#8a93a3] italic mt-1 truncate">"{sponsor.tagline}"</p>
                  )}
                  {sponsor.tournament_id && (
                    <p className="text-[10px] text-[#8a93a3] mt-0.5 truncate">{getTournamentName(sponsor.tournament_id)}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Sponsor Dialog */}
      <Dialog open={createOpen} onOpenChange={o=>{setCreateOpen(o);if(!o) resetCreate();}}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-sponsor-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Award className="h-5 w-5 text-[#f5c518]"/>Add Sponsor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Tournament */}
            <div>
              <Label className="text-[#8a93a3] mb-1 block">Tournament *</Label>
              <select value={form.tournament_id} onChange={e=>setForm({...form,tournament_id:e.target.value})}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-2 text-sm">
                <option value="">Select tournament</option>
                {tournaments.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            {/* Member search */}
            <div>
              <Label className="text-[#8a93a3] mb-1 block">Sponsor (Member) *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]"/>
                <Input value={memberSearch} onChange={e=>handleMemberSearch(e.target.value)}
                  placeholder="Search by name or phone..."
                  className="pl-10 bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"/>
              </div>
              {(memberSearching || memberResults.length > 0) && !selectedMember && (
                <div className="mt-1 bg-[#1d2230] border border-[#252a37] rounded-lg overflow-hidden max-h-36 overflow-y-auto">
                  {memberSearching && <p className="text-xs text-[#8a93a3] text-center py-2">Searching...</p>}
                  {memberResults.map(m=>(
                    <div key={m.id} onClick={()=>{setSelectedMember(m);setMemberSearch(m.full_name||`${m.first_name} ${m.last_name||''}`.trim());setMemberResults([]);}}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#252a37]">
                      <div className="h-6 w-6 rounded-full bg-[#252a37] flex items-center justify-center text-[#f5c518] text-xs font-bold flex-shrink-0">
                        {(m.full_name||m.first_name)?.[0]||'M'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-[#f0f2f7] truncate">{m.full_name||`${m.first_name} ${m.last_name||''}`.trim()}</p>
                        {m.contact_phone&&<p className="text-xs text-[#8a93a3] font-mono">{m.contact_phone}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {selectedMember && (
                <div className="mt-1 flex items-center gap-2 px-3 py-2 bg-[rgba(245,197,24,0.08)] border border-[rgba(245,197,24,0.3)] rounded-lg">
                  <span className="text-sm text-[#f5c518] flex-1">{selectedMember.full_name}</span>
                  <button onClick={()=>{setSelectedMember(null);setMemberSearch('');}} className="text-[#8a93a3] hover:text-[#ef4444] text-sm">×</button>
                </div>
              )}
            </div>

            {/* Team */}
            <div>
              <Label className="text-[#8a93a3] mb-1 block">Team to Sponsor *</Label>
              <select value={form.team_id} onChange={e=>setForm({...form,team_id:e.target.value})}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-2 text-sm">
                <option value="">Select team</option>
                {teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            {/* Tier + Tagline */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#8a93a3] mb-1 block">Tier</Label>
                <select value={form.type_id} onChange={e=>setForm({...form,type_id:e.target.value})}
                  className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-2 text-sm">
                  <option value="">Select tier</option>
                  {TIER_OPTIONS.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[#8a93a3] mb-1 block">Tagline</Label>
                <Input value={form.tagline} onChange={e=>setForm({...form,tagline:e.target.value})}
                  placeholder="Optional" className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"/>
              </div>
            </div>

            <div>
              <Label className="text-[#8a93a3] mb-1 block">Website URL</Label>
              <Input value={form.website_url} onChange={e=>setForm({...form,website_url:e.target.value})}
                placeholder="https://..." className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreateOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
              {creating ? 'Adding...' : 'Add Sponsor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sponsor Dialog */}
      <Dialog open={!!editTarget} onOpenChange={o => { if (!o) setEditTarget(null); }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm" data-testid="edit-sponsor-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#f5c518]"/>Edit Sponsor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Logo upload */}
            <div>
              <Label className="text-[#8a93a3] mb-2 block">Sponsor Logo</Label>
              <div onClick={() => editLogoRef.current?.click()}
                className="relative w-full h-24 rounded-xl bg-[#10131c] border-2 border-dashed border-[#252a37] hover:border-[rgba(245,197,24,0.4)] cursor-pointer overflow-hidden flex items-center justify-center transition-colors">
                {editLogoPreview ? (
                  <>
                    <img src={editLogoPreview} className="w-full h-full object-contain p-2" alt="logo"/>
                    <button type="button" onClick={e=>{e.stopPropagation();setEditLogoPreview(null);setEditLogoB64(null);setEditForm(f=>({...f,logo_url:''}));}}
                      className="absolute top-1 right-1 h-5 w-5 bg-[#1d2230] rounded-full flex items-center justify-center text-[#8a93a3] hover:text-[#ef4444] text-xs">✕</button>
                  </>
                ) : (
                  <div className="text-center">
                    <Upload className="h-5 w-5 text-[#8a93a3] mx-auto mb-1"/>
                    <p className="text-xs text-[#8a93a3]">Click to upload sponsor logo</p>
                    <p className="text-[10px] text-[#8a93a3]/60">PNG, JPG · Max 2MB</p>
                  </div>
                )}
              </div>
              <input type="file" accept="image/*" ref={editLogoRef} hidden onChange={handleEditLogoChange}/>
            </div>
            <div ref={nameSugRef} className="relative">
              <Label className="text-[#8a93a3]">Name * <span className="text-[10px] font-normal">(type to search members)</span></Label>
              <Input value={editForm.name}
                onChange={e => {
                  setEditForm({...editForm, name: e.target.value});
                  fetchNameSuggestions(e.target.value);
                }}
                onBlur={() => setTimeout(() => setShowNameSug(false), 200)}
                onFocus={() => nameSuggestions.length > 0 && setShowNameSug(true)}
                placeholder="Type member name or search..."
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="edit-sponsor-name-input"
                autoFocus/>
              {/* Autocomplete dropdown */}
              {showNameSug && nameSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-[#1d2230] border border-[#252a37] rounded-xl shadow-xl overflow-hidden">
                  {nameSuggestions.map(p => {
                    const name = p.full_name || `${p.first_name||''} ${p.last_name||''}`.trim();
                    return (
                      <button key={p.id} type="button"
                        onMouseDown={e => {
                          e.preventDefault();
                          setEditForm({...editForm, name});
                          setShowNameSug(false);
                          setNameSuggestions([]);
                        }}
                        className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#252a37] transition-colors">
                        <div className="h-7 w-7 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-[11px] text-white bg-[#3b82f6]">
                          {name[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#f0f2f7]">{name}</p>
                          {p.sports?.length > 0 && <p className="text-[10px] text-[#8a93a3]">{p.sports.join(', ')}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <Label className="text-[#8a93a3]">Team Name</Label>
              <Input value={editForm.team_name} onChange={e => setEditForm({...editForm, team_name: e.target.value})}
                placeholder="Team name shown in TV ticker (e.g. Kalinga Kangaru)"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="edit-sponsor-team-name-input"/>
            </div>
            <div>
              <Label className="text-[#8a93a3]">Tagline</Label>
              <Input value={editForm.tagline} onChange={e => setEditForm({...editForm, tagline: e.target.value})}
                placeholder="e.g., Proud sponsor of Team Alpha"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"/>
            </div>
            <div>
              <Label className="text-[#8a93a3]">Website URL</Label>
              <Input value={editForm.website_url} onChange={e => setEditForm({...editForm, website_url: e.target.value})}
                placeholder="https://..."
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"/>
            </div>
            <div>
              <Label className="text-[#8a93a3]">Tier</Label>
              <select value={editForm.type_id} onChange={e => setEditForm({...editForm, type_id: e.target.value})}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-2 text-sm mt-1">
                <option value="">Select tier</option>
                {[{id:'title',name:'Title Sponsor'},{id:'gold',name:'Gold Sponsor'},{id:'silver',name:'Silver Sponsor'},{id:'team',name:'Team Sponsor'}].map(t=>(
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving || !editForm.name.trim()}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="edit-sponsor-save-button">
              {editSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
